import { useCallback, useRef } from 'react';
import { Dimensions } from 'react-native';
import { Flex, List, RemoteImage, Typography, type ListRef } from '@banhala/ui-kit';
import type { AssembleReviewCardItem } from '../types';
import { useOpenDeeplink } from '../../../../../hooks';
import { bridge } from '../../../../../utils';
import type { AnyObject } from '../../../../../types';

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_HORIZONTAL_MARGIN = 12;

interface ReviewCardProps {
  items: AssembleReviewCardItem[];
  selectedIndex: number;
  onPageChange: (index: number) => void;
  listRef: React.RefObject<ListRef<AssembleReviewCardItem> | null>;
  logging: AnyObject;
  logDisabled?: boolean;
}

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 50 };

/** 리뷰 카드 — 가로 페이징, 양방향 동기화용 ref 제공 */
export const ReviewCard = ({
  items,
  selectedIndex,
  onPageChange,
  listRef,
  logging,
  logDisabled,
}: ReviewCardProps) => {
  const { openDeeplink } = useOpenDeeplink();
  const isScrollByUserRef = useRef(true);
  const displayedRef = useRef(new Set<number>());

  const logReviewEvent = useCallback(
    (name: string, item: AssembleReviewCardItem) => {
      if (logDisabled) return;
      bridge.logAnalyticsEvent({
        name,
        analytics: {
          ...logging.analytics,
          ...item.logging?.analytics,
        },
        ably_only_log: {
          ...logging.ably_only_log,
          ...item.logging?.ably_only_log,
        },
      });
    },
    [logging, logDisabled],
  );

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: Array<{ index: number | null; isViewable: boolean; item?: AssembleReviewCardItem }> }) => {
      // DISPLAY_REVIEW 로깅
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index == null || !isViewable || !item) return;
        if (displayedRef.current.has(index)) return;
        displayedRef.current.add(index);
        logReviewEvent('DISPLAY_REVIEW', item);
      });

      // 페이지 변경 감지 (사용자 스와이프)
      if (!isScrollByUserRef.current) return;
      const visibleIndex = viewableItems.find(v => v.isViewable)?.index;
      if (visibleIndex != null && visibleIndex !== selectedIndex) {
        // SWIPE_REVIEW_CARD 로깅
        const swipedItem = items[visibleIndex];
        if (swipedItem) {
          logReviewEvent('SWIPE_REVIEW_CARD', swipedItem);
        }
        onPageChange(visibleIndex);
      }
    },
    [onPageChange, selectedIndex, items, logReviewEvent],
  );

  const handleCardPress = useCallback(
    (item: AssembleReviewCardItem) => {
      // CLICK_REVIEW 로깅
      logReviewEvent('CLICK_REVIEW', item);
      if (item.deeplink) {
        openDeeplink(item.deeplink);
      }
    },
    [openDeeplink, logReviewEvent],
  );

  const handleImagePress = useCallback(
    (item: AssembleReviewCardItem) => {
      // CLICK_REVIEW_IMAGE 로깅
      logReviewEvent('CLICK_REVIEW_IMAGE', item);
      if (item.image?.deeplink) {
        openDeeplink(item.image.deeplink);
      }
    },
    [openDeeplink, logReviewEvent],
  );

  if (items.length === 0) return null;

  const renderItem = ({ item }: { item: AssembleReviewCardItem }) => (
    <Flex width={SCREEN_WIDTH} padding={{ horizontal: CARD_HORIZONTAL_MARGIN }}>
      <ReviewCardItem
        item={item}
        onPress={() => handleCardPress(item)}
        onImagePress={() => handleImagePress(item)}
      />
    </Flex>
  );

  return (
    <Flex margin={{ bottom: 12 }}>
      <List<AssembleReviewCardItem>
        ref={listRef}
        horizontal
        pagingEnabled
        data={items}
        renderItem={renderItem}
        keyExtractor={(_, index) => `review-card-${index}`}
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={VIEWABILITY_CONFIG}
      />
    </Flex>
  );
};

// --- 개별 리뷰 카드 아이템 ---

interface ReviewCardItemProps {
  item: AssembleReviewCardItem;
  onPress: () => void;
  onImagePress: () => void;
}

const ReviewCardItem = ({ item, onPress, onImagePress }: ReviewCardItemProps) => {
  return (
    <Flex
      backgroundColorValue="#F5F5F5"
      borderRadius={8}
      padding={{ horizontal: 12, vertical: 12 }}
      onPress={onPress}
    >
      <Flex.Row alignItems="center">
        {item.image?.url && (
          <Flex
            onPress={onImagePress}
            margin={{ right: 12 }}
            width={80}
            height={80}
            borderRadius={4}
            overflow="hidden"
          >
            <RemoteImage
              uri={item.image.url}
              width={80}
              height={80}
            />
          </Flex>
        )}
        <Flex flex={1}>
          <Flex.Row alignItems="center">
            {item.title && (
              <Typography type="subtitle2" color="content_primary" numberOfLines={1}>
                {item.title}
              </Typography>
            )}
            {item.is_best && (
              <Flex
                backgroundColorValue="#FFDFE4"
                borderRadius={2}
                padding={{ horizontal: 4 }}
                margin={{ left: 4 }}
              >
                <Typography type="subtitle6" rawColor="#FF5160">
                  BEST
                </Typography>
              </Flex>
            )}
          </Flex.Row>
          {item.description && (
            <Flex margin={{ top: 8 }}>
              <Typography
                type="body4"
                color="content_secondary"
                numberOfLines={2}
              >
                {item.description}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Flex.Row>
    </Flex>
  );
};
